import "@saleor/utils/prototypeExtension/sortAlphabeticallyByKey";

import KeyboardArrowDown from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUp from "@material-ui/icons/KeyboardArrowUp";
import { useUser } from "@saleor/auth";
import { FIELD_TYPE } from "@saleor/changeOrderExport/components/OrderFilterSection/constant";
import {
  PermissionEnum,
  useFilterDomesticOrderConfirmationCompanyQuery,
  useFilterRequireAttentionMaterialLazyQuery,
  useGetSalesOrgBySoldToQuery
} from "@saleor/graphql";
import { useFormik } from "formik";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

const useDeliveryReport = ({ handleChangeFilter }) => {
  const { listCompany, company, user } = useUser();

  const isCustomer = useMemo(
    () =>
      !!user?.userPermissions.find(
        e => e.code === PermissionEnum.ALL_REPORT_CUSTOMER
      ),
    [user?.userPermissions]
  );

  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  const soldToId = window.localStorage.getItem("SOLD_TO_ID");
  const selectedCompany = listCompany?.find(
    e => e.id === soldToId || e.soldToCode === company
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      soldTo: isCustomer
        ? `${selectedCompany?.soldToCode} - ${selectedCompany?.soldToName}`
        : "",
      material: [],
      orderCreateDate: {
        gte: yesterday.toISOString().split("T")[0] || null,
        lte: today.toISOString().split("T")[0] || null
      },
      saleOrganization: [],
      poNo: "",
      soNo: ""
    },
    onSubmit: () => {
      const errorFields = [];
      // if (!formik.values.soldTo.length) {
      //   errorFields.push({
      //     fieldName: "soldTo",
      //     value: "",
      //     error: "โปรดระบุ Sold-to"
      //   });
      // }
      // if (!formik.values.orderCreateDate.gte) {
      //   errorFields.push({
      //     fieldName: "orderCreateDate.gte",
      //     value: "",
      //     error: "โปรดระบุวันที่"
      //   });
      // }
      // if (!formik.values.orderCreateDate.lte) {
      //   errorFields.push({
      //     fieldName: "orderCreateDate.lte",
      //     value: "",
      //     error: "โปรดระบุวันที่"
      //   });
      // }
      if (!formik.values.saleOrganization.length) {
        errorFields.push({
          fieldName: "saleOrganization",
          value: "",
          error: "โปรดระบุข้อมูล"
        });
      }
      // if (!formik.values.poNo) {
      //   errorFields.push({
      //     fieldName: "poNo",
      //     value: "",
      //     error: "โปรดระบุข้อมูล"
      //   });
      // }
      if (errorFields.length) {
        errorFields.forEach(e => {
          if (!e.value) {
            formik.setFieldError(e.fieldName, e.error);
          }
        });
      } else {
        handleChangeFilter({
          soldToCode: formik.values.soldTo?.split("-")[0]?.trim() || "",
          matNo: formik.values.material?.[0] || "",
          saleOrgCode: formik.values.saleOrganization,
          bu: formik.values.saleOrganization.some(
            code =>
              saleOrgChoices.find(choice => choice.value === code)?.bu === "PP"
          )
            ? "PP"
            : "CIP",
          soNo: formik.values.soNo,
          poNo: formik.values.poNo,
          shippingPoint: "",
          deliveryDate: formik.values.orderCreateDate
        });
      }
    }
  });

  const intl = useIntl();
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [isClean, setIsClean] = useState<boolean>(false);

  const { data: saleOrgs } = useGetSalesOrgBySoldToQuery({
    variables: {
      soldToCode: formik?.values?.soldTo?.split("-")[0]?.trim() || ""
    },
    fetchPolicy: "no-cache"
  });

  const saleOrgChoices = useMemo(
    () =>
      (saleOrgs?.getSalesOrgBySoldTo ?? [])
        .map(e => ({
          label: `${e.code} - ${e.shortName}`,
          bu: `${e.businessUnit.name}`,
          value: `${e.code}`
        }))
        .sortAlphabeticallyByKey("bu"),
    [saleOrgs]
  );

  const defaultSaleOrganization = useMemo(() => {
    const value = saleOrgChoices?.map(salesOrg => salesOrg.value) ?? [];
    return value;
  }, [saleOrgChoices]);

  useEffect(() => {
    formik.setValues(prev => ({
      ...prev,
      saleOrganization: defaultSaleOrganization
    }));
  }, [defaultSaleOrganization]);

  const {
    data: confirmationConpanies
  } = useFilterDomesticOrderConfirmationCompanyQuery();

  const ArrowIconComponent = useMemo(
    () => (isOpen ? KeyboardArrowUp : KeyboardArrowDown),
    [isOpen]
  );

  const handleCleanForm = useCallback(() => {
    setIsClean(true);
    formik.resetForm();
    handleChangeFilter({});
    setTimeout(() => {
      formik.setValues(prev => ({
        ...prev,
        saleOrganization: defaultSaleOrganization
      }));
      setIsClean(false);
    });
  }, [defaultSaleOrganization, handleChangeFilter]);

  const handleCleanDates = useCallback(() => {
    formik.setValues({
      ...formik.values,
      orderCreateDate: {
        gte: null,
        lte: null
      }
    });
  }, [formik]);

  const [
    getMaterialCustomerOrder,
    { data: fetchMaterialCustomerOrder, loading: loadingMaterialCustomerOrder }
  ] = useFilterRequireAttentionMaterialLazyQuery();

  const formData = useMemo(
    () => [
      {
        title: intl.formatMessage({
          defaultMessage: "Sold-to"
        }),
        // subTitle: "(เลือกค้นหา Sold-to ได้ไม่เกิน 10 รายการ)",
        name: "soldTo",
        placeholder: intl.formatMessage({
          defaultMessage: "ค้นหา Sold to..."
        }),
        // required: true,
        type: !isCustomer
          ? FIELD_TYPE.SUGGESTION_INPUT
          : FIELD_TYPE.SINGLE_INPUT,
        disabled: isCustomer,
        maxLength: 35
      },
      {
        title: intl.formatMessage({
          defaultMessage: "Material Code / Description"
        }),
        subTitle: "(เลือกค้นหาได้ไม่เกิน 1 รายการ)",
        name: "material",
        placeholder: intl.formatMessage({
          defaultMessage: "ค้นหา Material Code / Description"
        }),
        type: FIELD_TYPE.SUGGESTION_INPUT
      },
      {
        title: intl.formatMessage({ defaultMessage: "วันที่ส่งมอบ" }),
        name: "orderCreateDate",
        type: FIELD_TYPE.DATEPICKER
      },
      {
        title: intl.formatMessage({ defaultMessage: "Sales Organization" }),
        name: "saleOrganization",
        placeholder: intl.formatMessage({
          defaultMessage: "เลือก Sales Organization"
        }),
        type: FIELD_TYPE.MULTIPLE_SELECT,
        required: true,
        options: saleOrgChoices
      },
      {
        title: intl.formatMessage({
          defaultMessage: "PO No."
        }),
        // required: true,
        name: "poNo",
        placeholder: intl.formatMessage({
          defaultMessage: "ระบุ PO No."
        }),
        type: FIELD_TYPE.SINGLE_INPUT,
        maxLength: 35
      },
      {
        title: intl.formatMessage({
          defaultMessage: "S/O No."
        }),
        name: "soNo",
        placeholder: intl.formatMessage({
          defaultMessage: "ระบุ S/O No."
        }),
        type: FIELD_TYPE.SINGLE_INPUT,
        maxLength: 10
      }
    ],
    [confirmationConpanies, saleOrgChoices]
  );

  const setDataFormik = data => {
    formik.setValues(data);
  };

  return useMemo(
    () => ({
      formik,
      setDataFormik,
      isOpen,
      setIsOpen,
      isClean,
      setIsClean,
      ArrowIconComponent,
      handleCleanForm,
      handleCleanDates,
      formData,
      getMaterialCustomerOrder,
      fetchMaterialCustomerOrder,
      loadingMaterialCustomerOrder
    }),
    [
      formik,
      isOpen,
      setIsOpen,
      isClean,
      setIsClean,
      ArrowIconComponent,
      handleCleanForm,
      handleCleanDates,
      formData,
      getMaterialCustomerOrder,
      fetchMaterialCustomerOrder,
      loadingMaterialCustomerOrder
    ]
  );
};

export default useDeliveryReport;
